import React, { Fragment, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { Box, Grid, useMediaQuery, useTheme } from "@mui/material"
import type { CartesianMarkerProps } from "@nivo/core"
import type { DatumValue, Serie } from "@nivo/line"
import { ResponsiveLine } from "@nivo/line"
import { Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import type { CustomSwitchedModels } from "../../../types/custom/projects"
import { getTickInterval, truncateString } from "../../../utils/genericHelpers"
import { LoadingContainer } from "../../core/LoadingContainer"
import { ModelType } from "../../status/ModelType"
import { GraphHeader } from "../GraphHeader"

type Props = {
  title?: string
  subTitle?: React.ReactElement
  data?: Serie[]
  graphHeight: number
  isLoading: boolean
}

export function RequestsLineGraph(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { title, subTitle, data, graphHeight, isLoading } = props

  const theme = getTheme()
  const MuiTheme = useTheme()

  const isLgDown = useMediaQuery(MuiTheme.breakpoints.down("lg"))

  const colors: Record<string, string> = {
    Successful: theme.palette.green.background[1],
    Failed: theme.palette.red.background[1],
  }

  //get markers for model state history
  const markers = useMemo(() => {
    if (data && data.length > 0) {
      const temp: { axis: string; value: DatumValue; lineStyle: Record<string, unknown> }[] = []
      data[1]?.data.forEach((item) => {
        if (item.modelsSwitched && item.modelsSwitched.length > 0) {
          temp.push({
            axis: "x",
            value: item.x as DatumValue,
            lineStyle: { stroke: theme.palette.gray.background[1], strokeWidth: 2, strokeDasharray: "20,10,5,5,5,10" },
          })
        }
      })
      return temp
    }
    return []
  }, [data, theme.palette.gray.background])

  const activityTooltip = (
    createdAt: string,
    uuid: string,
    name?: string,
    before_state?: string,
    after_state?: string,
  ): React.ReactElement => {
    const stateChangeString =
      before_state === "live" || (before_state === "challenger" && after_state === "disabled")
        ? t("lineCharts.modelState.demoted")
        : before_state === "disabled" || (before_state === "challenger" && after_state === "live")
          ? t("lineCharts.modelState.promoted")
          : ""

    return (
      <Grid container direction="row" spacing={1}>
        <Grid item>
          <Typography variant="p" variantColor={2}>
            <strong>{moment(createdAt).format("h:mm A")}</strong>
          </Typography>
        </Grid>

        <Grid item direction="column" spacing={1}>
          <Typography variant="p" variantColor={2}>
            <strong>{name ?? truncateString(uuid, 12) + " " + t("lineCharts.modelState.deleted")}</strong>{" "}
            {stateChangeString} to <ModelType modelState={after_state} size="small" />
          </Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={"charts-paper"}>
      <GraphHeader title={title} padding="1rem" />

      <Box style={{ height: graphHeight }} px={2}>
        {(!data || data.length < 1) && !isLoading ? (
          <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
            <Typography variant="label-bold" variantColor={2}>
              {t("lineCharts.emptyState.noDataToDisplay")}
            </Typography>
          </Grid>
        ) : (
          <Fragment>
            {!!subTitle && (
              <Grid item xs={12}>
                {subTitle}
              </Grid>
            )}

            {isLoading || !data ? (
              <LoadingContainer />
            ) : (
              <ResponsiveLine
                data={data}
                margin={{ top: 20, right: 0, bottom: 60, left: 60 }}
                xScale={{ type: "time", format: "native", precision: "day", useUTC: false }}
                theme={NivoTheme(theme)}
                xFormat="time:%a %d %b"
                yScale={{ type: "linear", min: 0, max: "auto", stacked: false, reverse: false }}
                curve="monotoneX"
                colors={(line) => colors[line.id] || theme.palette.gray.background[0]}
                markers={markers as CartesianMarkerProps<DatumValue>[]}
                sliceTooltip={({ slice }) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const switchStates = slice.points[0]?.data as any
                  return (
                    <div className={"chart-tooltip"}>
                      <div>
                        <div style={{ display: "flex" }}>
                          <Typography variant="h3-bold" variantColor={2}>
                            {slice.points[0]?.data.xFormatted}
                          </Typography>
                        </div>

                        <Box mt={1} />

                        <Typography variant="h3-bold" variantColor={2}>
                          {t("lineCharts.tooltip.summary")}
                        </Typography>
                        <table style={{ borderCollapse: "collapse" }}>
                          <tbody>
                            {slice.points.map((point) => (
                              <tr key={point.id}>
                                <td style={{ padding: "3px 5px" }}>
                                  <span
                                    key="chip"
                                    style={{
                                      display: "block",
                                      width: "12px",
                                      height: "12px",
                                      background: point.serieColor,
                                      borderRadius: "100%",
                                    }}
                                  />
                                </td>
                                <td style={{ padding: "3px 5px" }}>
                                  <Typography variant="p" variantColor={2}>
                                    {t(`chartLabels.${(point.serieId as string).toLowerCase()}`)}
                                  </Typography>
                                </td>
                                <td style={{ padding: "3px 5px" }}>
                                  <Typography variant="label-bold" variantColor={2}>
                                    {point.data[`yFormatted`]}
                                  </Typography>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {!!switchStates?.modelsSwitched.length && (
                          <Box mt={1}>
                            <Typography variant="label-bold" variantColor={2}>
                              <strong>{t("lineCharts.tooltip.activity")}</strong>
                            </Typography>

                            {switchStates.modelsSwitched.map((item: CustomSwitchedModels) => (
                              <Box mb={1} key={item.created_at}>
                                {activityTooltip(
                                  item.created_at,
                                  item.model,
                                  item.model_name,
                                  item.before_state,
                                  item.after_state,
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </div>
                    </div>
                  )
                }}
                axisTop={null}
                axisRight={null}
                enableArea={true}
                axisBottom={{
                  // adjusting the tick values based on selected date range
                  tickValues: getTickInterval(data[0]?.data?.length ?? 0),
                  format: "%a %d %b",
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: isLgDown ? -35 : -45,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: t("lineCharts.legend.numberOfRequests"),
                  legendOffset: -55,
                  legendPosition: "middle",
                  // Only display integer values
                  format: (e) => {
                    if (Math.floor(e) === e) return e
                  },
                }}
                pointSize={0}
                pointColor={{ theme: "background" }}
                pointBorderWidth={2}
                pointBorderColor={{ from: "serieColor" }}
                enablePointLabel={true}
                pointLabel={"data.y"}
                pointLabelYOffset={-6}
                useMesh={true}
                enableSlices="x"
              />
            )}
          </Fragment>
        )}
      </Box>
    </div>
  )
}
